import { useState, useEffect, useRef } from 'react';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Input, Switch, Button, Select, MenuItem } from '@dsi/react-eds';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { aiSettingsService, AiProviderConfig } from './aiSettingsService';

type Provider = 'aws' | 'azure' | 'openai';
type AwsAccess = 'subscription' | 'direct';

const EMPTY: AiProviderConfig = {
  provider: 'aws',
  endpoint: '',
  apiKey: '',
  apiSecret: '',
  sessionToken: '',
  model: '',
  region: '',
  apiVersion: ''
};

export const AiSettingsTab: React.FC = () => {
  const { addNotifications } = useSwobNotifications();
  const [showSecret, setShowSecret] = useState(false);

  const [llmMode, setLlmMode] = useState<'local' | 'enterprise'>('local');
  const [provider, setProvider] = useState<Provider>('aws');
  const [awsAccess, setAwsAccess] = useState<AwsAccess>('subscription');
  // Per-provider configs kept in state so switching providers preserves each.
  const [configs, setConfigs] = useState<Record<Provider, AiProviderConfig>>({
    aws: { ...EMPTY, provider: 'aws' },
    azure: { ...EMPTY, provider: 'azure' },
    openai: { ...EMPTY, provider: 'openai' }
  });
  const [historyEnabled, setHistoryEnabled] = useState(true);
  const [mcpEnabled, setMcpEnabled] = useState(true);

  // Harden against browser/password-manager autofill. EDS Input does not forward
  // autoComplete/name to the underlying <input>, so we set the attributes directly
  // on the rendered DOM nodes. Re-runs when the visible fields change.
  const formRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = formRef.current;
    if (!root) return;
    const inputs = root.querySelectorAll('input');
    inputs.forEach((el, i) => {
      el.setAttribute('autocomplete', 'off');
      // A non-credential name + one-time-code hint stops password managers.
      if (!el.getAttribute('name')) el.setAttribute('name', `llmcfg_${i}`);
      el.setAttribute('data-lpignore', 'true');
      el.setAttribute('data-1p-ignore', 'true');
      if (el.getAttribute('type') === 'password') {
        el.setAttribute('autocomplete', 'new-password');
      }
    });
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await aiSettingsService.get();
      const mode = data.llmMode === 'local' ? 'local' : data.llmMode ? 'enterprise' : 'local';
      setLlmMode(mode as 'local' | 'enterprise');
      setProvider((data.enterpriseProvider as Provider) || 'aws');
      setAwsAccess((data.enterpriseAwsAccessLevel as AwsAccess) || 'subscription');
      // Prefill each provider from the saved providers[] list
      const next = {
        aws: { ...EMPTY, provider: 'aws' as Provider },
        azure: { ...EMPTY, provider: 'azure' as Provider },
        openai: { ...EMPTY, provider: 'openai' as Provider }
      };
      (data.providers || []).forEach((p) => {
        const pv = p.provider as Provider;
        if (['aws', 'azure', 'openai'].includes(pv)) {
          next[pv] = { ...EMPTY, ...p, provider: pv };
        }
      });
      setConfigs(next);
      setHistoryEnabled(data.historyEnabled ?? true);
      setMcpEnabled(data.mcpToolsEnabled ?? true);
    } catch (e) {
      console.error('Failed to load AI settings', e);
    }
  };

  const setField = (field: keyof AiProviderConfig, value: string) => {
    setConfigs((prev) => ({ ...prev, [provider]: { ...prev[provider], [field]: value } }));
  };

  const cfg = configs[provider];

  const onSave = async () => {
    try {
      const c = configs[provider];
      await aiSettingsService.update({
        llmMode,
        enterpriseProvider: provider,
        enterpriseAwsAccessLevel: provider === 'aws' ? awsAccess : undefined,
        enterpriseEndpoint: c.endpoint,
        enterpriseApiKey: c.apiKey,
        enterpriseApiSecret: c.apiSecret,
        enterpriseSessionToken: c.sessionToken,
        enterpriseModel: c.model,
        enterpriseRegion: c.region,
        enterpriseApiVersion: c.apiVersion,
        historyEnabled,
        mcpToolsEnabled: mcpEnabled
      });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'AI settings saved successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      loadSettings();
    } catch (e) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: 'Failed to save AI settings',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  const single = (value: any): string => (Array.isArray(value) ? value[0] : value);

  const field = (id: string, label: string, key: keyof AiProviderConfig, placeholder = '', secret = false) => (
    <div style={{ marginBottom: '16px' }}>
      <ReactEDSWrapper
        component={Input}
        id={id}
        // Unique non-credential name + autoComplete off so the browser password
        // manager does NOT autofill email/password into these fields.
        name={`llmcfg_${id}`}
        autoComplete="off"
        data-lpignore="true"
        type={secret && !showSecret ? 'password' : 'text'}
        label={label}
        placeholder={placeholder}
        value={(cfg[key] as string) || ''}
        onChange={(e: any) => setField(key, e?.target?.value ?? '')}
        icon={secret ? `icon ${showSecret ? 'icon-invisible' : 'icon-eye'}` : undefined}
        onIconClick={secret ? () => setShowSecret(!showSecret) : undefined}
        style={{ width: '100%' }}
      />
    </div>
  );

  return (
    <div ref={formRef} style={{ padding: '16px', maxWidth: '700px' }}>
      {/* Mode */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '8px' }}>LLM Mode</label>
        <ReactEDSWrapper
          component={Select}
          id="llmMode"
          type="single"
          value={llmMode}
          onChange={(v: any) => setLlmMode(single(v) as 'local' | 'enterprise')}
          style={{ width: '100%' }}
        >
          <ReactEDSWrapper component={MenuItem} value="local">
            Local SLM (on-prem)
          </ReactEDSWrapper>
          <ReactEDSWrapper component={MenuItem} value="enterprise">
            Enterprise LLM
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </div>

      {llmMode === 'enterprise' && (
        <>
          {/* Provider selector */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px' }}>Provider</label>
            <ReactEDSWrapper
              component={Select}
              id="provider"
              type="single"
              value={provider}
              onChange={(v: any) => setProvider(single(v) as Provider)}
              style={{ width: '100%' }}
            >
              <ReactEDSWrapper component={MenuItem} value="aws">
                AWS (Bedrock)
              </ReactEDSWrapper>
              <ReactEDSWrapper component={MenuItem} value="azure">
                Azure OpenAI
              </ReactEDSWrapper>
              <ReactEDSWrapper component={MenuItem} value="openai">
                OpenAI-compatible
              </ReactEDSWrapper>
            </ReactEDSWrapper>
          </div>

          {/* Per-provider fields */}
          {provider === 'aws' && (
            <>
              {/* AWS access level: two ways to reach Amazon Bedrock */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px' }}>Access level</label>
                <ReactEDSWrapper
                  component={Select}
                  id="awsAccess"
                  type="single"
                  value={awsAccess}
                  onChange={(v: any) => setAwsAccess(single(v) as AwsAccess)}
                  style={{ width: '100%' }}
                >
                  <ReactEDSWrapper component={MenuItem} value="subscription">
                    Subscription (temporary credentials)
                  </ReactEDSWrapper>
                  <ReactEDSWrapper component={MenuItem} value="direct">
                    Direct model (endpoint + token)
                  </ReactEDSWrapper>
                </ReactEDSWrapper>
              </div>

              {awsAccess === 'subscription' && (
                <>
                  {field('awsRegion', 'AWS Region', 'region', 'us-east-1')}
                  {field('awsModel', 'Model ID', 'model', 'us.anthropic.claude-sonnet-4-20250514-v1:0')}
                  {field('awsKey', 'AWS Access Key ID (blank = IAM role)', 'apiKey', '', true)}
                  {field('awsSecret', 'AWS Secret Access Key', 'apiSecret', '', true)}
                  {field('awsSession', 'AWS Session Token (temporary creds)', 'sessionToken', '', true)}
                </>
              )}
              {awsAccess === 'direct' && (
                <>
                  {field('awsEndpoint', 'Endpoint URL', 'endpoint', 'https://bedrock-runtime.us-east-1.amazonaws.com')}
                  {field('awsDirectModel', 'Model ID', 'model', 'us.anthropic.claude-sonnet-4-20250514-v1:0')}
                  {field('awsToken', 'API Token', 'apiKey', 'Bedrock API key / bearer token', true)}
                </>
              )}
            </>
          )}
          {provider === 'azure' && (
            <>
              {field('azEndpoint', 'Azure Endpoint', 'endpoint', 'https://<resource>.openai.azure.com')}
              {field('azDeployment', 'Deployment (model) name', 'model', 'gpt-4o')}
              {field('azVersion', 'API Version', 'apiVersion', '2024-06-01')}
              {field('azKey', 'API Key', 'apiKey', '', true)}
            </>
          )}
          {provider === 'openai' && (
            <>
              {field('oaEndpoint', 'Base URL', 'endpoint', 'https://api.openai.com/v1 (or your LLM URL)')}
              {field('oaModel', 'Model ID', 'model', 'gpt-4o')}
              {field('oaKey', 'API Key', 'apiKey', '', true)}
            </>
          )}
        </>
      )}

      {/* Prefs */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ marginRight: '12px' }}>Conversation history</label>
        <ReactEDSWrapper
          component={Switch}
          id="historyEnabled"
          defaultChecked={historyEnabled}
          onChange={() => setHistoryEnabled(!historyEnabled)}
        />
      </div>

      {llmMode === 'enterprise' && (
        <div style={{ marginBottom: '24px' }}>
          <label style={{ marginRight: '12px' }}>MCP tool calling</label>
          <ReactEDSWrapper
            component={Switch}
            id="mcpToolsEnabled"
            defaultChecked={mcpEnabled}
            onChange={() => setMcpEnabled(!mcpEnabled)}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <ReactEDSWrapper component={Button} primary onClick={onSave}>
          Save
        </ReactEDSWrapper>
        <ReactEDSWrapper component={Button} onClick={loadSettings}>
          Reset
        </ReactEDSWrapper>
      </div>
    </div>
  );
};
